// Custom API client para admin endpoints
const API_BASE_URL = '/api/v2';

class AdminApiClient {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Access denied (${response.status}). Administrative privileges required.`);
      }
      
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }
}

const adminApiClient = new AdminApiClient();

// Interfaces baseadas no backend domain models
export interface PerfilRelatorioConfig {
  nome: string;
  descricao_resumida: string;
  descricao_completa: string;
  pontos_fortes: PontoForteConfig[];
  areas_desenvolvimento: AreaDesenvolvimentoConfig[];
  plano_acao: PlanoAcaoConfig;
  estrategias_especificas: EstrategiasEspecificasConfig;
}

export interface PontoForteConfig {
  titulo: string;
  descricao: string;
  exemplo_pratico: string;
}

export interface AreaDesenvolvimentoConfig {
  titulo: string;
  descricao: string;
  impacto: string;
  sugestoes: string[];
}

export interface TarefaPlanoAcaoConfig {
  tarefa: string;
  descricao: string;
  area_melhoria: string;
  complexidade: string;
}

export interface PlanoAcaoConfig {
  '30_dias': TarefaPlanoAcaoConfig[];
  '90_dias': TarefaPlanoAcaoConfig[];
  '6_meses': TarefaPlanoAcaoConfig[];
}

export interface EstrategiasEspecificasConfig {
  networking: string[];
  apresentacoes: string[];
  negociacoes: string[];
  lideranca: string[];
}

export interface InsumosRelatoriosContent {
  versao: string;
  data_criacao: string;
  descricao: string;
  perfis: Record<string, PerfilRelatorioConfig>;
}

export interface ContentVersion {
  id: string;
  type: string;
  version: string;
  description: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  metadata: Record<string, string>;
}

export interface ActiveContentInfo {
  active_version: ContentVersion;
  version: string;
  last_updated: number;
}

// Cache para evitar requests desnecessários
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live em milliseconds
}

class ContentSnapshotCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultTTL = 5 * 60 * 1000; // 5 minutos

  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }
}

class ContentSnapshotService {
  private cache = new ContentSnapshotCache();
  private readonly CACHE_KEYS = {
    ACTIVE_INFO: 'active_content_info',
    REPORT_CONTENTS: 'report_contents_',
  };

  /**
   * Busca informações da versão ativa do conteúdo
   */
  async fetchActiveVersionInfo(): Promise<ActiveContentInfo> {
    const cacheKey = this.CACHE_KEYS.ACTIVE_INFO;
    const cached = this.cache.get<ActiveContentInfo>(cacheKey);
    
    if (cached) {
      return cached;
    }

    try {
      const data = await adminApiClient.get<ActiveContentInfo>('/admin/active/info');
      
      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Erro ao buscar informações da versão ativa:', error);
      throw new Error('Falha ao carregar informações da versão ativa do conteúdo');
    }
  }

  /**
   * Busca o conteúdo dos relatórios para uma versão específica
   */
  async fetchReportContents(versionId: string): Promise<InsumosRelatoriosContent> {
    const cacheKey = this.CACHE_KEYS.REPORT_CONTENTS + versionId;
    const cached = this.cache.get<InsumosRelatoriosContent>(cacheKey);
    
    if (cached) {
      return cached;
    }

    try {
      const response = await adminApiClient.get<{report_contents: InsumosRelatoriosContent}>(`/admin/versions/${versionId}/report-contents`);
      const data = response.report_contents;
      
      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Erro ao buscar conteúdo dos relatórios para versão ${versionId}:`, error);
      throw new Error(`Falha ao carregar conteúdo dos relatórios para a versão ${versionId}`);
    }
  }

  /**
   * Busca todos os perfis da versão ativa
   */
  async fetchActiveProfiles(): Promise<Record<string, PerfilRelatorioConfig>> {
    try {
      // Primeiro busca info da versão ativa
      const activeInfo = await this.fetchActiveVersionInfo();
      
      // Depois busca o conteúdo dos relatórios
      const reportContents = await this.fetchReportContents(activeInfo.active_version.id);
      
      return reportContents.perfis;
    } catch (error) {
      console.error('Erro ao buscar perfis ativos:', error);
      throw error;
    }
  }

  /**
   * Converte perfis do snapshot para array com IDs
   */
  async fetchActiveProfilesArray(): Promise<Array<PerfilRelatorioConfig & { id: string }>> {
    const profiles = await this.fetchActiveProfiles();
    
    return Object.entries(profiles).map(([id, profile]) => ({
      id,
      ...profile,
    }));
  }

  /**
   * Busca um perfil específico por ID
   */
  async fetchProfileById(profileId: string): Promise<PerfilRelatorioConfig | null> {
    try {
      const profiles = await this.fetchActiveProfiles();
      return profiles[profileId] || null;
    } catch (error) {
      console.error(`Erro ao buscar perfil ${profileId}:`, error);
      return null;
    }
  }

  /**
   * Força refresh do cache
   */
  async refreshCache(): Promise<void> {
    this.cache.clear();
    // Pre-carrega dados principais
    await this.fetchActiveVersionInfo();
  }

  /**
   * Verifica se o serviço está disponível (usuário autenticado e tem permissões)
   */
  async checkServiceAvailability(): Promise<boolean> {
    try {
      await this.fetchActiveVersionInfo();
      return true;
    } catch (error) {
      // Se der erro 401/403, significa que não tem permissão
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('401') || errorMessage.includes('403')) {
        return false;
      }
      // Outros erros podem ser temporários
      throw error;
    }
  }

  /**
   * Limpa cache específico de uma versão
   */
  invalidateVersionCache(versionId: string): void {
    this.cache.invalidate(this.CACHE_KEYS.REPORT_CONTENTS + versionId);
  }

  /**
   * Obtém informações do cache atual
   */
  getCacheInfo(): { 
    activeInfoCached: boolean; 
    totalCacheEntries: number; 
    lastActiveInfoFetch?: number;
  } {
    const activeInfoEntry = this.cache.get(this.CACHE_KEYS.ACTIVE_INFO);
    
    return {
      activeInfoCached: !!activeInfoEntry,
      totalCacheEntries: (this.cache as any).cache.size,
      lastActiveInfoFetch: activeInfoEntry ? Date.now() - (activeInfoEntry as any).timestamp : undefined,
    };
  }
}

// Instância singleton
export const contentSnapshotService = new ContentSnapshotService();

// Export das interfaces principais para uso nos componentes
export type {
  PerfilRelatorioConfig,
  ContentVersion,
  ActiveContentInfo,
  InsumosRelatoriosContent,
};